import React, { useEffect, useMemo, useState } from 'react';
import { useMonitoringReady } from '../../../../components/MonitoringObserver';
import {
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Statistic,
  Tooltip,
  Typography,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import {
  CREATING,
  fetchAdminAIPlayers,
  fetchBenchmarkCoverageDiagnostics,
  fetchBenchmarkCoverageSummary,
  fetchBenchmarkSuggestion,
  fetchBiasReports,
  fetchPersons,
  fetchSession,
  generateBenchmarkSuggestion,
  generateSessionBiasReports,
  generateSessionMarketRegimes,
  recomputeAnalysis,
  recomputePnLDistributions,
  recomputeSessionBenchmarkCoverage,
  HISTORICAL_SCENARIO,
  SCENARIO_TYPES,
  selectors,
  SESSION_STATUSES,
  updateSessionMarketsView,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingScreen } from 'components';
import Player from './Player';
import Players from './Players';
import RegimeVisualizerDrawer from './RegimeVisualizerDrawer';
import AdminIndividualReport from './AdminIndividualReport';
import BenchmarkCoverageDiagnosticsDrawer from './BenchmarkCoverageDiagnosticsDrawer';
import BenchmarkRecomputeDrawer from './BenchmarkRecomputeDrawer';
import SessionPremiumModelCard from '../../SimplifiedOptions/SessionPremiumModelCard';
import MarketsViewSelect from '../../MarketsViewSelect';
import { formatters } from 'services';
import { LegacyVisibilityEditor } from '../../../../features/instrumentVisibility/legacy';

const { Title } = Typography;

const BIAS_PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic (Claude Opus 4.8)' },
  { value: 'openai', label: 'OpenAI (GPT-5.5 Pro)' },
];

const SUGGESTION_PENDING = 0;
const SUGGESTION_COMPUTED = 1;
const SUGGESTION_ERROR = 2;
const SUGGESTION_CANCELED = 3;

const formatCoverageTimestamp = (value?: string | null) => {
  if (!value) {
    return 'Not computed yet';
  }

  return value.replace('T', ' ').replace(/\.\d+/, '').replace('+00:00', ' UTC');
};

const Session = () => {
  const dispatch = useDispatch();
  const { path } = useRouteMatch();
  const { session_id } = useParams<{ session_id: string }>();

  const id = parseInt(session_id);
  const session = useSelector(selectors.sessionById(id));
  useMonitoringReady(id, session?.id === id, 'legacy');
  const biasWaiting = useSelector(selectors.biasReportsWaiting);
  const biasGenerating = useSelector(selectors.biasGenerating);
  const biasReportItems = useSelector(selectors.biasReports);
  const biasPlayerOptions = useSelector(selectors.adminAIPlayers);
  const biasPlayersWaiting = useSelector(selectors.adminAIPlayersWaiting);
  // Players that already have a COMPUTED bias report (status === 1) — drives the
  // ✓ cue. Updated live by the bias_report_generated socket listener.
  const biasDonePlayerIds = useMemo(
    () =>
      new Set(
        Object.values(biasReportItems)
          .filter(r => r.status === 1)
          .map(r => r.player),
      ),
    [biasReportItems],
  );
  const [biasProvider, setBiasProvider] = useState('anthropic');
  const [biasPlayerIds, setBiasPlayerIds] = useState<number[]>([]);
  const regimeWaiting = useSelector(selectors.marketRegimesWaiting);
  const [regimeProvider, setRegimeProvider] = useState('anthropic');
  const [suggestionProvider, setSuggestionProvider] = useState('anthropic');
  const [regimeDrawerOpen, setRegimeDrawerOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [benchmarkRecomputeOpen, setBenchmarkRecomputeOpen] = useState(false);
  const sessionLoaded = !!session;
  const scenarioType = session?.scenario_type;
  const summaryTimestamp =
    session?.benchmark_coverage_summary?.last_computed_at;
  const diagnosticsTimestamp =
    session?.benchmark_coverage_diagnostics?.summary?.last_computed_at;
  const diagnosticsWaiting = session?.benchmark_coverage_diagnostics_waiting;
  const hasDiagnostics = !!session?.benchmark_coverage_diagnostics;

  useEffect(() => {
    if (session) {
      return;
    }
    dispatch(fetchSession({ id }));
    dispatch(fetchPersons({}));
  }, [dispatch, id, session]);

  // Player list for the bias-analysis selector (same source as the AI tab).
  useEffect(() => {
    dispatch(fetchAdminAIPlayers({ session_id: id }));
    // Load existing bias reports so the ✓ "already generated" cue is populated
    // on refresh (live updates arrive via the bias_report_generated socket).
    dispatch(fetchBiasReports({ session_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (!session) {
      return;
    }
    // Both modes now: the summary is mode-aware server-side (historical = published counts,
    // real-time = fresh coverage over the session's elapsed dates -- admin-only, no publish).
    dispatch(fetchBenchmarkCoverageSummary({ session_id: id }));
  }, [dispatch, id, scenarioType]);

  useEffect(() => {
    if (!sessionLoaded) {
      return;
    }
    dispatch(fetchBenchmarkSuggestion({ session_id: id }));
  }, [dispatch, id, sessionLoaded]);

  useEffect(() => {
    if (!diagnosticsOpen || !sessionLoaded) {
      return;
    }

    if (
      diagnosticsWaiting ||
      (hasDiagnostics && summaryTimestamp === diagnosticsTimestamp)
    ) {
      return;
    }

    dispatch(fetchBenchmarkCoverageDiagnostics({ session_id: id }));
  }, [
    diagnosticsOpen,
    diagnosticsTimestamp,
    diagnosticsWaiting,
    dispatch,
    hasDiagnostics,
    id,
    sessionLoaded,
    summaryTimestamp,
  ]);

  if (!session) {
    return <LoadingScreen />;
  }

  const sessionSummary = (
    <>
      <Title level={3}>Session {session_id}</Title>
      <Typography.Text>
        Session {SESSION_STATUSES[session.status]}
      </Typography.Text>
      <br />
      <Typography.Text>
        {SCENARIO_TYPES[session.scenario_type]} scenario
      </Typography.Text>
      <br />
      <Typography.Text>
        {session.klass_name} , class ID: {session.klass}
      </Typography.Text>
      <br />
      <Typography.Text>{session.trainer_name}</Typography.Text>
      <br />
      <Typography.Text>
        Scenario date: {formatters.dateOnly(session.scenario_date || '-')}
      </Typography.Text>
      <br />
      <Typography.Text>
        Finish date: {formatters.dateOnly(session.finish_date || '-')}
      </Typography.Text>
    </>
  );

  const benchmarkCoverageSummary = session.benchmark_coverage_summary;
  const isHistoricalSession = session.scenario_type === HISTORICAL_SCENARIO;
  const suggestion = session.benchmark_suggestion;
  const suggestionPending =
    session.benchmark_suggestion_waiting ||
    suggestion?.status === SUGGESTION_PENDING;
  const suggestionStale = !!(suggestion?.stale || suggestion?.is_stale);
  const suggestionStatus = suggestionStale
    ? 'Stale'
    : suggestion?.status === SUGGESTION_COMPUTED
      ? 'Ready'
      : suggestion?.status === SUGGESTION_CANCELED
        ? 'Canceled'
        : suggestion?.status === SUGGESTION_ERROR
          ? 'Error'
          : suggestionPending
            ? 'Generating'
            : 'No suggestion yet';
  const suggestionDisabledReason =
    (session.underlying_assets?.length ?? 0) === 0
      ? 'Set the instrument universe before generating.'
      : isHistoricalSession &&
          benchmarkCoverageSummary &&
          benchmarkCoverageSummary.eligible_index_funds_count === 0
        ? 'No benchmark-eligible index funds for this session.'
        : undefined;

  return (
    <div>
      {sessionSummary}
      <SessionPremiumModelCard sessionId={id} />
      {session.status !== CREATING &&
        [0, 4].includes(session.which_markets) && (
          <Card
            title="Instrument Universe"
            size="small"
            style={{ marginTop: 16 }}
          >
            <LegacyVisibilityEditor session={session} />
          </Card>
        )}
      <Card
        size="small"
        title="Player Markets view"
        style={{ marginTop: 16 }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <MarketsViewSelect
            value={session.which_markets ?? 0}
            onChange={whichMarkets =>
              dispatch(
                updateSessionMarketsView({
                  session_id: session.id,
                  which_markets: whichMarkets,
                }),
              )
            }
          />
          <Typography.Text type="secondary">
            Changes the Markets tab for every player in this session,
            including players who are already connected.
          </Typography.Text>
        </div>
      </Card>
      <Card
        size="small"
        title="Benchmark suggestion"
        style={{ marginTop: 16 }}
        extra={
          <Tooltip title="Generates a persisted LLM-authored basket of index funds for this session's instrument universe. Admins see the overall rationale; trainers only see the basket.">
            <InfoCircleOutlined />
          </Tooltip>
        }
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Select
            value={suggestionProvider}
            onChange={setSuggestionProvider}
            style={{ minWidth: 220 }}
            options={BIAS_PROVIDER_OPTIONS}
          />
          <Button
            type="primary"
            loading={suggestionPending}
            disabled={!!suggestionDisabledReason || suggestionPending}
            onClick={() =>
              dispatch(
                generateBenchmarkSuggestion({
                  session_id: id,
                  provider: suggestionProvider,
                }),
              )
            }
          >
            {suggestion ? 'Regenerate suggestion' : 'Generate suggestion'}
          </Button>
          <Typography.Text type={suggestionStale ? 'warning' : 'secondary'}>
            {suggestionStatus}
          </Typography.Text>
        </div>
        {suggestionDisabledReason ? (
          <Typography.Text
            type="warning"
            style={{ display: 'block', marginTop: 8 }}
          >
            {suggestionDisabledReason}
          </Typography.Text>
        ) : null}
        {suggestion?.components?.length ? (
          <div style={{ marginTop: 12 }}>
            {suggestion.components.map(component => (
              <div
                key={`${component.ric}-${component.id}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '4px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Typography.Text>{component.ric}</Typography.Text>
                <Typography.Text strong>
                  {(component.weight * 100).toFixed(1)}%
                </Typography.Text>
              </div>
            ))}
          </div>
        ) : suggestion?.status === SUGGESTION_COMPUTED ? (
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 8 }}
          >
            No matching basket was returned.
          </Typography.Text>
        ) : null}
        {suggestion?.explanation ? (
          <Typography.Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
            {suggestion.explanation}
          </Typography.Paragraph>
        ) : null}
      </Card>
      <Card
        size="small"
        title="Bias analysis"
        style={{ marginTop: 16 }}
        extra={
          <Tooltip title="Runs an LLM behavioral-bias analysis for the selected players (from blotter actions, positions and price curves). Results stream into the trainer Analysis Studio › Bias tab as they complete. Intended for historical (replay) sessions.">
            <InfoCircleOutlined />
          </Tooltip>
        }
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Select
            mode="multiple"
            showSearch
            optionFilterProp="label"
            value={biasPlayerIds}
            onChange={value => setBiasPlayerIds(value as number[])}
            style={{ minWidth: 320, flex: 1 }}
            placeholder={
              biasPlayersWaiting ? 'Loading players…' : 'Select players'
            }
            options={biasPlayerOptions.map(p => ({
              label: biasDonePlayerIds.has(p.id)
                ? `✓ ${p.username}`
                : p.username,
              value: p.id,
            }))}
          />
          <Select
            value={biasProvider}
            onChange={setBiasProvider}
            style={{ minWidth: 220 }}
            options={BIAS_PROVIDER_OPTIONS}
          />
          <Button
            type="primary"
            loading={biasWaiting || biasGenerating}
            disabled={!biasPlayerIds.length || biasGenerating}
            onClick={() =>
              dispatch(
                generateSessionBiasReports({
                  session_id: id,
                  provider: biasProvider,
                  player_ids: biasPlayerIds,
                }),
              )
            }
          >
            {biasGenerating ? 'Generating…' : 'Compute bias analysis'}
          </Button>
        </div>
        <Typography.Text
          type="secondary"
          style={{ display: 'block', marginTop: 8 }}
        >
          Generates a bias report for each selected player. Any existing report
          for those players is replaced.
        </Typography.Text>
      </Card>

      {session.scenario_type === HISTORICAL_SCENARIO && (
        <>
          <Card
            size="small"
            title="Market regimes"
            style={{ marginTop: 16 }}
            extra={
              <Tooltip title="Labels each asset's price regime (uptrend / downtrend / range-bound / high-volatility / breakout / reversal) over the session, from the price curves. Session-level — a recompute replaces the prior result. Intended for historical (replay) sessions.">
                <InfoCircleOutlined />
              </Tooltip>
            }
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Select
                value={regimeProvider}
                onChange={setRegimeProvider}
                style={{ minWidth: 220 }}
                options={BIAS_PROVIDER_OPTIONS}
              />
              <Button
                type="primary"
                loading={regimeWaiting}
                onClick={() =>
                  dispatch(
                    generateSessionMarketRegimes({
                      session_id: id,
                      provider: regimeProvider,
                    }),
                  )
                }
              >
                Compute market regimes
              </Button>
              <Button onClick={() => setRegimeDrawerOpen(true)}>
                Visualize
              </Button>
            </div>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginTop: 8 }}
            >
              Segments each asset's price curve into regimes (price curves only
              in v1). Session-level — a recompute replaces the prior result.
            </Typography.Text>
          </Card>

          <RegimeVisualizerDrawer
            sessionId={id}
            open={regimeDrawerOpen}
            onClose={() => setRegimeDrawerOpen(false)}
          />
        </>
      )}
      {session.status !== CREATING && (
        <Card
          size="small"
          title="Analysis recompute"
          style={{ marginTop: 16 }}
          extra={
            <Tooltip title="Recomputes the per-player analysed metrics and concentrations, and the PnL distributions, for this session. Results stream into the trainer’s Analysis Studio (Risk & Drivers tabs).">
              <InfoCircleOutlined />
            </Tooltip>
          }
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Button
              loading={session.analysis_recompute_waiting}
              onClick={() => dispatch(recomputeAnalysis({ session_id: id }))}
            >
              Recompute analysis &amp; concentrations
            </Button>
            <Button
              loading={session.pnl_recompute_waiting}
              onClick={() =>
                dispatch(recomputePnLDistributions({ session_id: id }))
              }
            >
              Recompute PnL distributions
            </Button>
          </div>
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 8 }}
          >
            Runs in the background. Refreshed data appears in the trainer’s
            Analysis Studio the next time it is opened.
          </Typography.Text>
        </Card>
      )}
      {/* Benchmark Coverage. Historical: the trainer-offering eligibility gate (published, pushes to
          trainers on recompute). Real-time: admin-only coverage over the session's ELAPSED dates to
          support metrics/benchmark readjustment -- the trainer offering stays ungated, nothing is
          published. Rendered for both modes; content re-scoped by isHistoricalSession. */}
      <>
        <Card
          size="small"
          title="Benchmark Coverage"
          style={{ marginTop: 16 }}
          extra={
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Button onClick={() => setDiagnosticsOpen(true)}>
                Open Diagnostics
              </Button>
              <Button onClick={() => setBenchmarkRecomputeOpen(true)}>
                Recompute metrics vs benchmark
              </Button>
              <Button
                loading={
                  isHistoricalSession
                    ? session.eligible_benchmark_options_waiting
                    : diagnosticsWaiting
                }
                onClick={() => {
                  if (isHistoricalSession) {
                    dispatch(
                      recomputeSessionBenchmarkCoverage({ session_id: id }),
                    );
                  } else {
                    // RT: re-pull the fresh (server-computed) coverage; no publish task.
                    dispatch(fetchBenchmarkCoverageSummary({ session_id: id }));
                    dispatch(
                      fetchBenchmarkCoverageDiagnostics({ session_id: id }),
                    );
                  }
                }}
              >
                {isHistoricalSession
                  ? 'Recompute benchmark coverage'
                  : 'Refresh coverage'}
              </Button>
              <Tooltip
                title={
                  isHistoricalSession
                    ? 'Rechecks which benchmark funds and equities still have the required historical closes for this session, then pushes the updated option set to trainers. Admin EoD refresh actions also trigger this recompute.'
                    : 'Admin-only: rechecks benchmark fund/equity coverage over the dates this real-time session has elapsed through, to support metrics/benchmark readjustment. Does not change what trainers can pick.'
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
          }
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={
                  isHistoricalSession
                    ? 'Funds in trainer'
                    : 'Funds fully covered'
                }
                value={
                  benchmarkCoverageSummary
                    ? `${benchmarkCoverageSummary.eligible_index_funds_count} / ${benchmarkCoverageSummary.total_index_funds_count}`
                    : '-'
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={
                  isHistoricalSession
                    ? 'Equities in trainer'
                    : 'Equities fully covered'
                }
                value={
                  benchmarkCoverageSummary
                    ? `${benchmarkCoverageSummary.eligible_equities_count} / ${benchmarkCoverageSummary.total_equities_count}`
                    : '-'
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={isHistoricalSession ? 'Required dates' : 'Elapsed dates'}
                value={benchmarkCoverageSummary?.required_dates_count ?? '-'}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary">
                Last recomputed
              </Typography.Text>
              <div style={{ marginTop: 8 }}>
                <Typography.Text>
                  {isHistoricalSession
                    ? benchmarkCoverageSummary?.last_computed_at
                      ? formatCoverageTimestamp(
                          benchmarkCoverageSummary.last_computed_at,
                        )
                      : 'Not computed yet'
                    : 'Computed live'}
                </Typography.Text>
              </div>
            </Col>
          </Row>
        </Card>

        <BenchmarkCoverageDiagnosticsDrawer
          open={diagnosticsOpen}
          onClose={() => setDiagnosticsOpen(false)}
          sessionId={id}
        />
        <BenchmarkRecomputeDrawer
          open={benchmarkRecomputeOpen}
          onClose={() => setBenchmarkRecomputeOpen(false)}
          sessionId={id}
        />
      </>

      <div style={{ marginTop: 15 }}>
        {session.status === CREATING ? (
          <Card size="small">
            <Typography.Text>
              The rest of this page&apos;s content will be available after the
              session is started.
            </Typography.Text>
          </Card>
        ) : (
          <Switch>
            <Route path={`${path}/players/:player_id/individual-report`}>
              <AdminIndividualReport />
            </Route>
            <Route path={`${path}/players/:player_id`}>
              <Player />
            </Route>
            <Route path={`${path}/players`}>
              <Players />
            </Route>
            <Redirect to={`${path}/players`} />
          </Switch>
        )}
      </div>
    </div>
  );
};

export default Session;
